import { Input, Text, VStack } from '@chakra-ui/react';
import { Field as ChakraField } from '@chakra-ui/react/field';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useDeleteInitializationMaskingZoneMutation } from '@services/config/initializationMaskingZone.api';
import { useDeleteNoFlyZoneMutation } from '@services/config/noFlyZone.api';
import { useDeleteNoInitZoneMutation } from '@services/config/noInitZone.api';
import Yup from '@utils/validation/yup-extended';
import { Field, Formik } from 'formik';
import { Dispatch, SetStateAction } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Zone, ZoneTypeEnum } from '@/types/config/config.types';

type Props = {
  selectedZone: Zone | null;
  setSelectedZone: Dispatch<SetStateAction<Zone | null>>;
};

export function DeleteZoneModal({ selectedZone, setSelectedZone }: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const [deleteNIZ, { isLoading: isLoadingNIZ }] = useDeleteNoInitZoneMutation();
  const [deleteNFZ, { isLoading: isLoadingNFZ }] = useDeleteNoFlyZoneMutation();
  const [deleteIMZ, { isLoading: isLoadingIMZ }] = useDeleteInitializationMaskingZoneMutation();
  const isLoading = isLoadingNIZ || isLoadingNFZ || isLoadingIMZ;

  const deleteZone = (id: number) => {
    switch (selectedZone?.type) {
      case ZoneTypeEnum.NIZ:
        return deleteNIZ(id);
      case ZoneTypeEnum.NFZ:
        return deleteNFZ(id);
      case ZoneTypeEnum.IMZ:
        return deleteIMZ(id);
      default:
        return null;
    }
  };

  return (
    <Formik
      initialValues={{ zoneName: '' }}
      onSubmit={() => {
        selectedZone &&
          deleteZone(selectedZone.id)
            ?.unwrap()
            .then(() => {
              createToast(
                formatMessage({ id: 'components.zones.message.delete' }, { name: selectedZone.name }),
                ToastStatusEnum.SUCCESS,
              );
              setSelectedZone(null);
            });
      }}
      validationSchema={
        selectedZone &&
        Yup.object({
          zoneName: Yup.string().required().oneOf([selectedZone.name.toUpperCase()]),
        })
      }
      validateOnMount
    >
      {({ errors, touched, isValid, handleSubmit }) => {
        const confirmationName = selectedZone?.name.toUpperCase();
        return (
          <CockpitDialog
            open={selectedZone !== null}
            isLoading={isLoading}
            isDisabled={!isValid}
            onCancel={() => setSelectedZone(null)}
            onConfirm={handleSubmit}
            otherChildren={
              <VStack width="100%" gap={2} padding={3} border="2px solid" borderColor="neutral.800">
                <Text color="neutral.300" width="100%">
                  <FormattedMessage
                    id="components.zones.popup.descriptionDeleteConfirmation"
                    values={{ name: confirmationName }}
                  />
                </Text>
                <ChakraField.Root variant="cockpit" required invalid={touched.zoneName && !!errors.zoneName}>
                  <Field as={Input} placeholder={confirmationName} type="text" name="zoneName" id="zoneName" />
                </ChakraField.Root>
              </VStack>
            }
          >
            <Text color="neutral.100" width="100%" whiteSpace="pre-line">
              <FormattedMessage id="components.zones.popup.descriptionDelete" values={{ name: selectedZone?.name }} />
            </Text>
          </CockpitDialog>
        );
      }}
    </Formik>
  );
}
